import React, { PropsWithChildren, useState, useCallback, ContextType } from 'react';
import { debounce, DebouncedFunc } from 'lodash';
import type { ModalName, ModalType } from '../types';

export function useFullscreenModalState(): ContextType<typeof ModalRenderContext> {
    return React.useContext(ModalRenderContext);
}

export function useFullscreenModalControls(): ModalControls {
    const context = React.useContext(ModalWriteContext);

    if (!context) {
        throw new Error('Missing ModalWriteContext');
    }

    return context;
}

type ModalControls = {
    open: DebouncedFunc<(modal: ModalType<ModalName>) => void>;
    close: () => void;
};

export const ModalWriteContext = React.createContext<undefined | ModalControls>(undefined);

export const ModalRenderContext = React.createContext<undefined | ModalType<ModalName>>(undefined);

export function FullscreenModalContextProvider(
    props: PropsWithChildren<{ initialState?: ModalType<ModalName> }>
): JSX.Element {
    const [currentModal, setCurrentModal] = useState<undefined | ModalType<ModalName>>(
        props.initialState
    );

    const open = useCallback(
        (modal: ModalType<ModalName>) => {
            setCurrentModal(modal);
        },
        [setCurrentModal]
    );

    const close = useCallback(() => {
        setCurrentModal(undefined);
    }, [setCurrentModal]);

    const memoizedValue = React.useMemo(() => {
        return {
            open: debounce(open, 500, { leading: true }),
            close,
        };
    }, [open, close]);

    return (
        <ModalWriteContext.Provider value={memoizedValue}>
            <ModalRenderContext.Provider value={currentModal}>
                {props.children}
            </ModalRenderContext.Provider>
        </ModalWriteContext.Provider>
    );
}
